// Choice views and the pure draft controller that turns a choice plus the player's
// answers into exactly one ordinary command (docs/ui-v2-contract.md section 5).
// The client never decides legality; it only enforces the descriptor's own bounds.

import type { ActionSchema, FieldSchema } from '../protocol/wire';
import type { ChoiceStep, FormEditor, FormValue, Observation, Scalar, RecordForm, PickOption } from '../protocol/ui';
import { humanize, ownsDecision, type TableModel } from './table.ts';
import { validateRecord } from './record.ts';

export interface ParameterField { name: string; label: string; schema: FieldSchema; initial: unknown }

export interface ChoiceView {
  id: string;
  scope?: 'decision' | 'auxiliary';
  label: string;
  group: string;
  prompt: string | null;
  subjects: string[];
  action: { kind: string; data: Record<string, unknown> };
  steps: ChoiceStep[];
  confirm: string | null;
  review?: { label: string; value: string; objectId?: string }[];
  /** ui-1 fallback: editable fields named by a legal action's `parameters`. */
  parameters: ParameterField[];
  /** Fields the action schema declares as arrays: a single pick writes `[value]` there (ui-2 section 5). */
  arrayFields?: string[];
}

const NON_SUBJECT_FIELDS = new Set(['index', 'count', 'amount', 'quantity']);

/** Resolve only this filtered observation's shared domains; never a prior seat/revision. */
function resolveForm(form: RecordForm, sets: Record<string, PickOption[]>): RecordForm {
  if (form.type === 'pick' && form.optionsRef) return { ...form, options: sets[form.optionsRef] ?? [] };
  if (form.type === 'array') return { ...form, items: resolveForm(form.items, sets) };
  if (form.type === 'object') return { ...form, fields: form.fields.map(f => resolveForm(f, sets)) };
  return form;
}
function resolveStep(step: ChoiceStep, sets: Record<string, PickOption[]>): ChoiceStep {
  if (step.type === 'record') return { ...step, form: resolveForm(step.form, sets) };
  if (step.type === 'pick') return { ...step, options: (step.optionsRef ? sets[step.optionsRef] ?? [] : step.options).map(o => o.then ? { ...o, then: o.then.map(s => resolveStep(s, sets)) } : o) };
  return step;
}

export function buildChoices(observation: Observation, schemas: ActionSchema[], objectIds: Set<string>): ChoiceView[] {
  if (Array.isArray(observation.choices) && observation.choices.length > 0) {
    return observation.choices.map(c => ({
      id: c.id, scope: c.scope ?? 'decision', label: c.label, group: c.group ?? humanize(c.action.kind), prompt: c.prompt ?? null,
      subjects: c.subjects ?? [], action: { kind: c.action.kind, data: { ...(c.action.data ?? {}) } },
      steps: (c.steps ?? []).map(s => resolveStep(s, observation.ui.optionSets ?? {})), confirm: c.confirm ?? null, review: c.review, parameters: [],
      arrayFields: (schemas.find(s => s.kind === c.action.kind)?.fields ?? []).filter(f => f.type === 'array').map(f => f.name),
    }));
  }
  // Fallback (ui-5.4): one stepless choice per legal action.
  return observation.legalActions.map((legal, i) => {
    const data = { ...(legal.action.data ?? {}) };
    const schema = schemas.find(s => s.kind === legal.action.kind);
    const parameters: ParameterField[] = (legal.parameters ?? []).flatMap(p => {
      const field = schema?.fields.find(f => f.name === p.name);
      return field ? [{ name: p.name, label: p.label, schema: field, initial: data[p.name] }] : [];
    });
    const subjects = new Set<string>();
    for (const [key, value] of Object.entries(data)) {
      if (NON_SUBJECT_FIELDS.has(key)) continue;
      if ((typeof value === 'string' || typeof value === 'number') && objectIds.has(String(value))) subjects.add(String(value));
    }
    return {
      id: `legal-${i}`, label: legal.label, group: humanize(legal.action.kind), prompt: null, subjects: [...subjects],
      action: { kind: legal.action.kind, data }, steps: [], confirm: null, parameters,
    };
  });
}

export type ChoiceContext = Pick<TableModel, 'selfSeat' | 'finished' | 'actor' | 'decisionOwners' | 'auxiliaryActions'>;
/** Eligibility belongs to this exact choice; one available auxiliary kind never unlocks others. */
export function choiceEligible(context: ChoiceContext, choice: Pick<ChoiceView, 'scope' | 'action'>): boolean {
  if (!context.selfSeat || context.selfSeat === 'spectator' || context.finished) return false;
  if (choice.scope === 'auxiliary') return context.auxiliaryActions?.includes(choice.action.kind) ?? false;
  return (choice.scope === undefined || choice.scope === 'decision') && ownsDecision(context);
}

export function groupChoices(choices: ChoiceView[]): { group: string; choices: ChoiceView[] }[] {
  const groups = new Map<string, ChoiceView[]>();
  for (const c of choices) groups.set(c.group, [...(groups.get(c.group) ?? []), c]);
  return [...groups].map(([group, list]) => ({ group, choices: list }));
}

// ---- Draft controller ----------------------------------------------------------------

export type StepValue = FormValue | undefined;

export interface Answer { step: ChoiceStep; value: StepValue; forced?: boolean }

export interface Draft {
  choice: ChoiceView;
  queue: ChoiceStep[];
  answers: Answer[];
  parameterValues: Record<string, unknown>;
}

export function startDraft(choice: ChoiceView): Draft {
  const parameterValues: Record<string, unknown> = {};
  for (const p of choice.parameters) parameterValues[p.name] = p.initial;
  return autoAdvance({ choice, queue: [...choice.steps], answers: [], parameterValues });
}

/** The only value a step allows, when there is exactly one: a single required option or a fixed allocation. */
export function forcedValue(step: ChoiceStep): StepValue | null {
  if (step.type === 'pick' && !step.expand && step.options.length === 1 && (step.min ?? 1) >= 1 && (step.max ?? 1) === 1) return step.options[0]!.value;
  if (step.type === 'allocate' && step.keys.every(k => k.min === k.max)) return step.keys.map(k => k.min);
  if (step.type === 'number' && step.min === step.max) return step.min;
  return null;
}

/** Answers forced steps so players only see real decisions; the review still lists them. */
export function autoAdvance(draft: Draft): Draft {
  let next = draft;
  for (let step = currentStep(next); step; step = currentStep(next)) {
    const value = forcedValue(step);
    if (value === null || validateStep(step, value)) break;
    next = answer(next, value, true);
  }
  return next;
}

/**
 * The action bar's instruction for a draft. A finished draft says Ready; its `confirm` warning is drawn separately,
 * so it must not also stand in as the instruction (that printed the warning twice).
 */
export function draftPrompt(step: ChoiceStep | null): string {
  return step ? step.label : 'Ready';
}

export function currentStep(draft: Draft): ChoiceStep | null {
  return draft.queue[draft.answers.length] ?? null;
}

export function isComplete(draft: Draft): boolean {
  return currentStep(draft) === null;
}

export function answer(draft: Draft, value: StepValue, forced = false): Draft {
  const step = currentStep(draft);
  if (!step) return draft;
  const error = validateStep(step, value);
  if (error) throw new Error(error);
  const queue = [...draft.queue];
  if (step.type === 'pick' && (step.max ?? 1) === 1 && !Array.isArray(value)) {
    const option = step.options.find(o => o.value === value);
    if (option?.then?.length) queue.splice(draft.answers.length + 1, 0, ...option.then);
  }
  const next = { ...draft, queue, answers: [...draft.answers, { step, value, forced }] };
  return forced ? next : autoAdvance(next);
}

/** Undoes the last real decision (and any forced steps after it). */
export function back(draft: Draft): Draft {
  let keep = draft.answers.length;
  while (keep > 0 && draft.answers[keep - 1]!.forced) keep--;
  if (keep === 0) return draft;
  keep--;
  let next: Draft = { ...startDraft(draft.choice), parameterValues: draft.parameterValues };
  for (const a of draft.answers.slice(0, keep)) if (!a.forced) next = answer(next, a.value);
  return next;
}

export function canGoBack(draft: Draft): boolean {
  return draft.answers.some(a => !a.forced);
}

export function setParameter(draft: Draft, name: string, value: unknown): Draft {
  return { ...draft, parameterValues: { ...draft.parameterValues, [name]: value } };
}

/** The command a completed draft submits. */
export function assemble(draft: Draft): { kind: string; data: Record<string, unknown> } {
  if (!isComplete(draft)) throw new Error('Complete every step.');
  const data: Record<string, unknown> = { ...draft.choice.action.data, ...draft.parameterValues };
  for (const { step, value } of draft.answers) {
    const problem = validateStep(step, value);
    if (problem) throw new Error(problem);
    if (step.type === 'form' && value === undefined && step.optional) continue;
    const isArray = draft.choice.arrayFields?.includes(step.field) ?? false;
    // A single pick that may be left empty (min 0) and was: a scalar field stays out of the command.
    if (step.type === 'pick' && (step.max ?? 1) === 1 && Array.isArray(value) && value.length === 0 && !isArray) continue;
    if (step.type === 'allocate' && step.encoding === 'object') {
      const values = value as number[];
      data[step.field] = Object.fromEntries(step.keys.map((k, i) => [k.key, values[i] ?? 0]));
    } else {
      // An array field always receives an array, even from a single pick.
      data[step.field] = step.type === 'pick' && isArray && !Array.isArray(value) ? [value] : value;
    }
  }
  if (draft.queue.some(step => step.type === 'form') && !withinAssembledFormBudget(data)) throw new Error('The complete form exceeds its work limit.');
  return { kind: draft.choice.action.kind, data };
}

/** One shared traversal over transmitted data, never reset per answer or form. */
export function withinAssembledFormBudget(data: unknown): boolean {
  let count = 0;
  const visit = (value: unknown): boolean => {
    if (++count > 4096) return false;
    if (Array.isArray(value)) { for (const item of value) if (!visit(item)) return false; }
    else if (value && typeof value === 'object') {
      for (const key of Object.keys(value)) {
        if (++count > 4096 || !visit((value as Record<string, unknown>)[key])) return false;
      }
    }
    return true;
  };
  return visit(data);
}

/** Returns a player-facing problem with a step value, or null when it fits the descriptor. */
export function validateStep(step: ChoiceStep, value: StepValue): string | null {
  switch (step.type) {
    case 'pick': {
      const min = step.min ?? 1, max = step.max ?? 1;
      const picked = Array.isArray(value) ? value : [value];
      if (picked.length < min) return `Choose at least ${min}.`;
      if (picked.length > max) return `Choose at most ${max}.`;
      if (!step.expand && picked.some(v => !step.options.some(o => o.value === v))) return 'Choose one of the listed options.';
      if (new Set(picked.map(String)).size !== picked.length) return 'Each option can be chosen once.';
      return null;
    }
    case 'allocate': return validateAllocation(step, value as number[]);
    case 'number': {
      const n = Number(value);
      if (!Number.isInteger(n)) return 'Enter a whole number.';
      if (n < step.min || n > step.max) return `Enter a number from ${step.min} to ${step.max}.`;
      return null;
    }
    case 'text': return String(value).length > step.maxLength ? `Use at most ${step.maxLength} characters.` : null;
    case 'form': return value === undefined && step.optional ? null : validateFormValue(step.editor, value);
    case 'record': return validateRecord(step.form, value);
  }
}

/** Explicit omissions remain absent; falsy and empty values are ordinary typed values. */
export function initialFormValue(editor: FormEditor): FormValue {
  switch (editor.type) {
    case 'object': return Object.fromEntries(editor.fields.filter(f => !f.optional).map(f => [f.field, initialFormValue(f.editor)]));
    case 'array': return Array.from({ length: editor.minItems }, () => initialFormValue(editor.items));
    case 'integer': return editor.options?.[0]?.value ?? editor.min;
    case 'string': return editor.options?.[0]?.value ?? '';
    case 'boolean': return editor.options?.[0]?.value ?? false;
  }
}

export function switchFormVariant(editor: Extract<FormEditor, { type: 'object' }>, value: unknown, index: number): FormValue {
  const variant = editor.discriminator?.variants[index];
  if (!variant || !editor.discriminator) throw new Error('Choose a listed variant.');
  const old = value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, FormValue> : {};
  const common = Object.fromEntries(editor.fields.filter(f => Object.hasOwn(old, f.field)).map(f => [f.field, old[f.field]!]));
  return { ...common, [editor.discriminator.field]: variant.value, ...Object.fromEntries(variant.fields.filter(f => !f.optional).map(f => [f.field, initialFormValue(f.editor)])) };
}

/** Defensive recursive validation before assembly, even for values edited outside the controls. */
export function validateFormValue(editor: FormEditor, value: unknown): string | null {
  let count = 0;
  const visit = (e: FormEditor, v: unknown, depth: number): string | null => {
    if (++count > 4096 || depth > 6) return 'The form exceeds its work limit.';
    if (e.type === 'object') {
      if (!v || typeof v !== 'object' || Array.isArray(v) || Object.getPrototypeOf(v) !== Object.prototype) return 'Enter an object using the form.';
      const object = v as Record<string, unknown>;
      const branch = e.discriminator?.variants.find(b => b.value === object[e.discriminator!.field]);
      if (e.discriminator && !branch) return 'Choose a listed variant.';
      const fields = [...e.fields, ...(branch?.fields ?? [])];
      const allowed = new Set([...fields.map(f => f.field), ...(e.discriminator ? [e.discriminator.field] : [])]);
      if (Object.keys(object).some(k => !allowed.has(k))) return 'Remove fields outside the selected variant.';
      for (const f of fields) {
        if (++count > 4096) return 'The form exceeds its work limit.';
        if (!Object.hasOwn(object, f.field)) { if (f.optional) continue; return `${f.label}: include a value.`; }
        const problem = visit(f.editor, object[f.field], depth + 1);
        if (problem) return `${f.label}: ${problem}`;
      }
      return null;
    }
    if (e.type === 'array') {
      if (!Array.isArray(v) || v.length < e.minItems || v.length > e.maxItems || v.length > 64) return `Choose ${e.minItems} to ${e.maxItems} entries.`;
      for (const item of v) { const problem = visit(e.items, item, depth + 1); if (problem) return problem; }
      return null;
    }
    if (e.type === 'string' && (typeof v !== 'string' || v.length > e.maxLength)) return `Use text of at most ${e.maxLength} characters.`;
    if (e.type === 'integer' && (typeof v !== 'number' || !Number.isInteger(v) || v < e.min || v > e.max)) return `Enter a whole number from ${e.min} to ${e.max}.`;
    if (e.type === 'boolean' && typeof v !== 'boolean') return 'Choose true or false.';
    if (e.options && !e.options.some(o => o.value === v)) return 'Choose a listed option.';
    return null;
  };
  return visit(editor, value, 0);
}

export function needsQueryCheck(draft: Draft): boolean { return draft.queue.some(s => s.check === 'query'); }

/** Action-schema structural checks are advisory, matching the host's JSON type/bounds checks. */
export function validateCommand(action: { kind: string; data: Record<string, unknown> }, schemas: ActionSchema[]): string | null {
  const schema = schemas.find(s => s.kind === action.kind);
  if (!schema) return 'This command is unavailable.';
  let count = 0;
  const visit = (s: FieldSchema, v: unknown, depth: number): boolean => {
    if (++count > 4096 || depth > 16) return false;
    if (s.type === 'string') return typeof v === 'string' && v.length <= (s.maxLength ?? 1024) && (!s.values || s.values.includes(v));
    if (s.type === 'integer') return typeof v === 'number' && Number.isSafeInteger(v) && v >= (s.minimum ?? -2147483648) && v <= (s.maximum ?? 2147483647);
    if (s.type === 'boolean') return typeof v === 'boolean';
    if (s.type === 'array') return Array.isArray(v) && v.length >= (s.minItems ?? 0) && v.length <= (s.maxItems ?? 1024) && !!s.items && v.every(x => visit(s.items!, x, depth + 1));
    if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
    const fields = s.fields ?? [], object = v as Record<string, unknown>;
    return Object.keys(object).every(k => fields.some(f => f.name === k)) && fields.every(f => Object.hasOwn(object, f.name) ? visit(f, object[f.name], depth + 1) : !f.required);
  };
  return visit({ name: 'data', type: 'object', fields: schema.fields }, action.data, 0) ? null : 'Complete every required field within its bounds.';
}

export function allocationBounds(key: { min: number; max: number; available?: number }): { min: number; max: number } {
  return { min: key.min, max: key.available === undefined ? key.max : Math.min(key.max, key.available) };
}

export function validateAllocation(step: Extract<ChoiceStep, { type: 'allocate' }>, values: number[]): string | null {
  if (!Array.isArray(values) || values.length !== step.keys.length) return 'Fill in every amount.';
  for (let i = 0; i < step.keys.length; i++) {
    const key = step.keys[i]!, v = values[i]!;
    const { min, max } = allocationBounds(key);
    if (!Number.isInteger(v)) return `${key.label}: enter a whole number.`;
    if (v < min) return `${key.label}: at least ${min}.`;
    if (v > max) return key.available !== undefined && v > key.available ? `${key.label}: you have ${key.available}.` : `${key.label}: at most ${max}.`;
  }
  if (step.sum) {
    const total = values.reduce((a, b) => a + b, 0);
    if (total < step.sum.min || total > step.sum.max)
      return step.sum.min === step.sum.max ? `The total must be ${step.sum.min}.` : `The total must be between ${step.sum.min} and ${step.sum.max}.`;
  }
  return null;
}

/**
 * The least any path through `steps` allocates (a pick branches into its options' follow-up steps),
 * and whether any path allocates at all. A path without an allocation costs nothing.
 */
export function leastCost(steps: ChoiceStep[]): { pays: boolean; least: number } {
  const [step, ...rest] = steps;
  if (!step) return { pays: false, least: 0 };
  if (step.type === 'allocate') {
    const after = leastCost(rest);
    const own = Math.max(step.keys.reduce((n, k) => n + k.min, 0), step.sum?.min ?? 0);
    return { pays: true, least: own + after.least };
  }
  if (step.type === 'pick' && !step.expand && (step.max ?? 1) === 1 && !step.ordered && step.options.some(o => o.then?.length)) {
    let pays = false, least = Infinity;
    for (const o of step.options) {
      const c = leastCost([...(o.then ?? []), ...rest]);
      pays ||= c.pays; least = Math.min(least, c.least);
    }
    return { pays, least: Number.isFinite(least) ? least : 0 };
  }
  return leastCost(rest);
}

/** Steps a choice still has after a target pre-answered its first pick with `value`. */
export function stepsAfter(choice: ChoiceView, value?: Scalar): ChoiceStep[] {
  const [first, ...rest] = choice.steps;
  if (value === undefined || !first || first.type !== 'pick') return choice.steps;
  return [...(first.options.find(o => o.value === value)?.then ?? []), ...rest];
}

/** Something on this path costs a payment, yet one way through it pays nothing. */
export function canBeFree(steps: ChoiceStep[]): boolean {
  const c = leastCost(steps);
  return c.pays && c.least === 0;
}

/**
 * The option of the draft's current single pick that costs nothing when it is the only such option and
 * another option costs more; otherwise null (ties and equal costs leave the choice to the player).
 */
export function freeDefault(draft: Draft): Scalar | null {
  const step = currentStep(draft);
  if (!step || step.type !== 'pick' || step.expand || (step.max ?? 1) !== 1 || step.ordered || step.options.length < 2) return null;
  const rest = draft.queue.slice(draft.answers.length + 1);
  const costs = step.options.map(o => ({ value: o.value, cost: leastCost([...(o.then ?? []), ...rest]) }));
  if (!costs.some(c => c.cost.pays)) return null;
  const free = costs.filter(c => c.cost.least === 0);
  return free.length === 1 && costs.some(c => c.cost.least > 0) ? free[0]!.value : null;
}

export function initialAllocation(step: Extract<ChoiceStep, { type: 'allocate' }>): number[] {
  if (step.initial && step.initial.length === step.keys.length) return [...step.initial];
  return step.keys.map(k => k.min);
}
/** JSON object member order is not part of a choice descriptor's meaning.
 * Preserve array order, since ordered options and programs depend on it. */
export function sameChoiceDescriptor(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((v, i) => sameChoiceDescriptor(v, b[i]));
  }
  const left = a as Record<string, unknown>, right = b as Record<string, unknown>;
  const keys = Object.keys(left).filter(k => left[k] !== undefined), other = Object.keys(right).filter(k => right[k] !== undefined);
  return keys.length === other.length && keys.every(k => Object.hasOwn(right, k) && sameChoiceDescriptor(left[k], right[k]));
}
